import type {
  LineInfo,
  StressMark,
  WordInfo,
} from "@/lib/tool-kit/poetry/pronounce";
import { phoneToIpa } from "@/lib/tool-kit/vocabulary/ipa";

/**
 * Alliteration, assonance and consonance, found by sound.
 *
 * Everything is decided from the CMU Pronouncing Dictionary's phones, never from letters,
 * so "knight" and "night" match (both begin with N) and "cat" and "city" do not (K and S).
 *
 * - **Alliteration** — words that begin with the same sound. The sound is the first
 *   phone, except that s + p, t or k counts as its own cluster, as in the Old English
 *   tradition: "stone" alliterates with "stand", not with "sing". A vowel-initial word
 *   alliterates with another beginning with the same vowel sound.
 * - **Assonance** — three or more words with the same stressed vowel sound.
 * - **Consonance** — three or more words ending in the same consonant sound. A closing
 *   /s/ or /z/ is ignored, since it mostly marks a plural or a verb ending.
 *
 * Matches must fall in one stanza or paragraph (a line break does not interrupt them)
 * within `gap` words of each other, and by default skip
 * function words (the, of, and…) and, for alliteration, words not stressed on their first
 * syllable — the sound a listener actually hears repeated.
 */

export type SoundKind = "alliteration" | "assonance" | "consonance";

export interface SoundOptions {
  /** Most words allowed between two matches that still belong to one run. */
  gap: number;
  /** Let function words (the, of, and…) take part. */
  functionWords: boolean;
  /** For alliteration: only words stressed on their first syllable (and one-syllable words). */
  firstSyllableStressed: boolean;
}

export const DEFAULT_SOUND_OPTIONS: SoundOptions = {
  gap: 2,
  functionWords: false,
  firstSyllableStressed: true,
};

export interface SoundRun {
  kind: SoundKind;
  /** Stable key for the sound, e.g. "P" or "S+P". */
  key: string;
  /** The sound in IPA, e.g. "p". */
  ipa: string;
  words: WordInfo[];
  /** For each word, its index into the lines passed in. */
  lines: number[];
}

export interface SoundAnalysis {
  runs: SoundRun[];
  /** Distinct words the dictionary lacks, which are left out. */
  skipped: string[];
}

const MIN_RUN: Record<SoundKind, number> = {
  alliteration: 2,
  assonance: 3,
  consonance: 3,
};

const CLUSTER_SECOND: ReadonlySet<string> = new Set(["P", "T", "K"]);
const RE_STRESS_DIGIT = /\d$/;

function isVowel(phone: string): boolean {
  return RE_STRESS_DIGIT.test(phone);
}

function ipaOfPhone(phone: string): string {
  const symbol = phone.replace(RE_STRESS_DIGIT, "");
  const digit = isVowel(phone) ? Number(phone.slice(-1)) : null;
  return phoneToIpa({
    symbol,
    stress: digit as 0 | 1 | 2 | null,
  });
}

interface Sound {
  key: string;
  ipa: string;
}

/** The sound a word begins with, or null when its pronunciation is unknown. */
export function initialSound(phones: readonly string[] | null): Sound | null {
  if (!phones || phones.length === 0) {
    return null;
  }
  const [first, second] = phones;
  if (first === "S" && second && CLUSTER_SECOND.has(second)) {
    return { key: `S+${second}`, ipa: `s${ipaOfPhone(second)}` };
  }
  return {
    key: first.replace(RE_STRESS_DIGIT, ""),
    ipa: ipaOfPhone(
      first.replace(RE_STRESS_DIGIT, "") + (isVowel(first) ? "1" : "")
    ),
  };
}

/** The vowel of the stressed syllable (or the first vowel if none is stressed). */
export function stressedVowel(phones: readonly string[] | null): Sound | null {
  if (!phones) {
    return null;
  }
  const vowels = phones.filter(isVowel);
  const chosen =
    vowels.find((p) => p.endsWith("1")) ??
    vowels.find((p) => p.endsWith("2")) ??
    vowels[0];
  if (!chosen) {
    return null;
  }
  return {
    key: chosen.replace(RE_STRESS_DIGIT, ""),
    ipa: ipaOfPhone(`${chosen.replace(RE_STRESS_DIGIT, "")}1`),
  };
}

/** The consonant a word ends on, ignoring a closing /s/ or /z/. */
export function finalConsonant(phones: readonly string[] | null): Sound | null {
  const last = phones?.at(-1);
  if (!last || isVowel(last) || last === "S" || last === "Z") {
    return null;
  }
  return { key: last, ipa: ipaOfPhone(last) };
}

function firstSyllableStress(word: WordInfo): StressMark {
  return word.reading.stress[0] ?? null;
}

function eligible(
  word: WordInfo,
  kind: SoundKind,
  options: SoundOptions
): boolean {
  if (!word.phones || word.reading.syllables === 0) {
    return false;
  }
  if (word.functionWord && !options.functionWords) {
    return false;
  }
  if (kind === "alliteration" && options.firstSyllableStressed) {
    const stress = firstSyllableStress(word);
    return word.reading.syllables === 1 || stress === 1 || stress === 2;
  }
  return true;
}

function soundOf(word: WordInfo, kind: SoundKind): Sound | null {
  if (kind === "alliteration") {
    return initialSound(word.phones);
  }
  return kind === "assonance"
    ? stressedVowel(word.phones)
    : finalConsonant(word.phones);
}

interface Entry {
  index: number;
  line: number;
  word: WordInfo;
  sound: Sound;
}

function chains(entries: readonly Entry[], gap: number): Entry[][] {
  const out: Entry[][] = [];
  let current: Entry[] = [];
  for (const entry of entries) {
    const previous = current.at(-1);
    if (previous && entry.index - previous.index - 1 > gap) {
      out.push(current);
      current = [];
    }
    current.push(entry);
  }
  if (current.length > 0) {
    out.push(current);
  }
  return out;
}

/** Runs through one stanza or paragraph: matches may cross a line break. */
function runsForBlock(
  block: readonly { line: LineInfo; index: number }[],
  kind: SoundKind,
  options: SoundOptions
): SoundRun[] {
  const byKey = new Map<string, Entry[]>();
  let position = 0;
  for (const { line, index: lineIndex } of block) {
    for (const word of line.words) {
      const sound = eligible(word, kind, options) ? soundOf(word, kind) : null;
      if (sound) {
        const entries = byKey.get(sound.key) ?? [];
        entries.push({ index: position, line: lineIndex, word, sound });
        byKey.set(sound.key, entries);
      }
      position += 1;
    }
  }
  const runs: SoundRun[] = [];
  for (const entries of byKey.values()) {
    for (const chain of chains(entries, options.gap)) {
      // The same word again is repetition, not a sound pattern.
      const distinct = new Set(chain.map((entry) => entry.word.key)).size;
      if (distinct >= MIN_RUN[kind]) {
        runs.push({
          kind,
          key: chain[0].sound.key,
          ipa: chain[0].sound.ipa,
          words: chain.map((entry) => entry.word),
          lines: chain.map((entry) => entry.line),
        });
      }
    }
  }
  return runs.sort(
    (a, b) => a.lines[0] - b.lines[0] || a.words[0].start - b.words[0].start
  );
}

/** Split lines into blocks at blank lines, remembering each line's index. */
function blocksOf(lines: readonly LineInfo[]) {
  const blocks: { line: LineInfo; index: number }[][] = [];
  let current: { line: LineInfo; index: number }[] = [];
  for (const [index, line] of lines.entries()) {
    if (line.text.trim().length === 0) {
      if (current.length > 0) {
        blocks.push(current);
        current = [];
      }
    } else {
      current.push({ line, index });
    }
  }
  if (current.length > 0) {
    blocks.push(current);
  }
  return blocks;
}

/** Every run of every kind in a poem or passage. */
export function findSounds(
  lines: readonly LineInfo[],
  options: SoundOptions = DEFAULT_SOUND_OPTIONS
): SoundAnalysis {
  const runs: SoundRun[] = [];
  const skipped = new Set<string>();
  for (const line of lines) {
    for (const word of line.words) {
      if (!word.phones && word.reading.syllables > 0) {
        skipped.add(word.key);
      }
    }
  }
  for (const block of blocksOf(lines)) {
    for (const kind of ["alliteration", "assonance", "consonance"] as const) {
      runs.push(...runsForBlock(block, kind, options));
    }
  }
  return { runs, skipped: [...skipped] };
}
